import { useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useChatContext } from "stream-chat-react";
import { RootState } from "store/store";
import { RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE } from "constants/date";
import * as CHANNEL_TYPES from "constants/channelType";
import { useChatFilterContext } from "contexts/ChatFilterContext";
import { usePaginatedChannels } from "hooks/usePaginatedChannels";
import authContext from "jwt_context&axios/authContext";
import { setUnrepliedChannelCount } from "store/slices/chat";
import { filterExcludedChannels } from "util/isChannelLastMessageExcluded";

export const useUnrepliedCount = () => {
  const { client } = useChatContext();
  const { authtoken } = useContext(authContext);
  const dispatch = useDispatch();
  const {
    filters: { EXCLUDE: excludeOptions },
  } = useChatFilterContext();

  const chatSearchValue = useSelector(
    (state: RootState) => state.chatSearch.value
  );

  const filterEmptyChatForAdmin = !chatSearchValue
    ? {
        last_non_broadcast_message_at: {
          $gte: RANDOM_DATE_FOR_CHANNELS_LAST_MESSAGE,
        },
      }
    : {};

  const { channels, hasNextPage, loadNextPage } = usePaginatedChannels(
    client,
    {
      type: "messaging",
      members: { $in: [client.userID!] },
      needs_reply: true,
      frozen: false,
      custom_type: {
        $in: Object.values(CHANNEL_TYPES).filter(
          (key) =>
            ![
              CHANNEL_TYPES.PRIVATE_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.ANNOUNCEMENTS_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.AI_CHANNEL_CUSTOM_TYPE,
              CHANNEL_TYPES.KB_SEARCH_CHANNEL_CUSTOM_TYPE,
            ].includes(key)
        ),
      },
      ...filterEmptyChatForAdmin,
    },
    {},
    {},
    () => {}
  );

  const visibleChannels = useMemo(
    () => filterExcludedChannels(channels, excludeOptions),
    [channels, excludeOptions]
  );

  const myChannelCount = visibleChannels.filter(
    (channel) => channel?.data?.assigned_agent === authtoken.uuid
  ).length;

  const count = hasNextPage ? 0 : visibleChannels.length;

  useEffect(() => {
    dispatch(
      setUnrepliedChannelCount({
        unrepliedChannelCount: count,
        myUnrepliedChannelCount: myChannelCount,
      })
    );
  }, [count, myChannelCount]);

  useEffect(() => {
    if (hasNextPage) {
      loadNextPage();
    }
  }, [channels.length, hasNextPage]);

  return {
    count,
    isLoading: hasNextPage,
    channels: visibleChannels,
    myChannelCount,
  };
};
